import React, { useRef } from 'react'
import * as yup from 'yup'
import { Formik, FormikHelpers } from 'formik'
import { isApolloError } from 'apollo-client'

// Constants
import { SKETCH_WEBSITE } from 'constants/sketchWebsite'

// Components
import Button from 'components/Button'
import FormikScrollToError from 'components/FormikScrollToError'
import Form from 'components/Form'
import { Title } from 'components/IndexLayout'

import Fieldset from '../../components/Fieldset'
import PaymentMethodForm from '../../components/PaymentMethodForm'
import { createFormikValidator } from '../../components/StripeField'
import BillingDetailsForm, {
  validationSchema,
} from '../../components/BillingDetailsForm'
import { Section } from '../../components/CreateWorkspaceCommon'

import {
  Wrapper,
  SubHeading,
  FormWrapper,
  SummaryWrapper,
  Layout,
  ButtonWrapper,
  CreditCardLogo,
  CreditCardWrapper,
} from './WorkspaceSubscribePaymentDetails.styles'
import Checkbox from 'components/Checkbox'

// Formik Validation
const VALIDATION_SCHEMA = yup
  .object()
  .shape({
    cardName: yup.string().trim().required('Name on Card is required'),
    tos: yup.mixed().oneOf([true], 'Terms of service must be agreed'),
  })
  .concat(validationSchema)

const SUPPORT_CREDIT_CARDS = ['amex', 'mastercard', 'visa'] as const

const INITIAL_VALUES = {
  name: '',
  email: '',
  address: '',
  city: '',
  country: 'US',
  postalCode: '',
  cardName: '',
  taxId: '',
  state: '',
  tos: false,

  /**
   * These fields are related with the credit-card
   * data, in order to get formik like validation
   * we need to include them in the "initial values"
   *
   * these values are only placeholders, in the end,
   * none of their values will be used
   */
  cardNumber: undefined,
  cardCvc: undefined,
  cardExpirationDate: undefined,
}

const STRIPE_ERRORS_TO_FIELDS: Record<string, keyof typeof INITIAL_VALUES> = {
  incorrect_cvc: 'cardCvc',
  processing_error: 'cardNumber',
  expired_card: 'cardExpirationDate',
  card_declined: 'cardNumber',
  incorrect_number: 'cardNumber',
  tax_id_invalid: 'taxId',
}

const stripeValidation = createFormikValidator(
  ['cardCvc', 'cardExpirationDate', 'cardNumber'],
  {
    cardNumber: 'Card Number is required',
    cardCvc: 'CVC is required',
    cardExpirationDate: 'Expiry Date is required',
  }
)

type FormValues = typeof INITIAL_VALUES
type SubmitValues = OmitSafe<
  FormValues,
  'cardNumber' | 'cardCvc' | 'cardExpirationDate'
>

interface WorkspaceSubscribePaymentDetailsProps {
  summaryUi: React.ReactElement
  onSubmit: (values: SubmitValues) => Promise<void>
}

export const WorkspaceSubscribePaymentDetails: React.FC<WorkspaceSubscribePaymentDetailsProps> = props => {
  const { onSubmit, summaryUi } = props
  const formRef = useRef<HTMLFormElement>(null)

  const handleOnSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    try {
      const {
        cardCvc,
        cardExpirationDate,
        cardNumber,
        ...correctValues
      } = values

      await onSubmit(correctValues)
    } catch (error) {
      if (isApolloError(error)) {
        error.graphQLErrors.forEach(({ extensions, message }) => {
          if (message === 'Invalid value for us_ein.') {
            actions.setFieldError(
              STRIPE_ERRORS_TO_FIELDS['tax_id_invalid'],
              'Please enter a valid VAT or tax ID number.'
            )

            return
          }

          const formField = STRIPE_ERRORS_TO_FIELDS[extensions?.stripeErrorCode]

          formField && actions.setFieldError(formField, message)
        })
      }
    } finally {
      actions.setSubmitting(false)
    }
  }

  return (
    <Wrapper>
      <Title>Set Up Payment</Title>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={VALIDATION_SCHEMA}
        validate={stripeValidation}
        onSubmit={handleOnSubmit}>
        {({ isSubmitting, touched, errors, handleChange, values }) => (
          <Form ref={formRef}>
            <Layout>
              <FormWrapper>
                <SubHeading size="medium">Payment Method</SubHeading>
                <CreditCardWrapper>
                  {SUPPORT_CREDIT_CARDS.map(card => (
                    <CreditCardLogo className={card} brand={card} key={card} />
                  ))}
                </CreditCardWrapper>
                <FormikScrollToError formRef={formRef} />
                <Fieldset>
                  <PaymentMethodForm<FormValues> />
                  <BillingDetailsForm<FormValues> />
                  <Section>
                    <label htmlFor="terms-of-service" className="sr-only">
                      I’ve read and agree to the Sketch Terms of Service and
                      order details.
                    </label>
                    <Form.Field
                      name="tos"
                      errorText={touched.tos ? errors.tos : undefined}>
                      <Checkbox
                        name="tos"
                        id="terms-of-service"
                        checked={values.tos}
                        onChange={handleChange}>
                        I’ve read and agree to the Sketch{' '}
                        <a
                          href={`${SKETCH_WEBSITE}/tos`}
                          target="_blank"
                          rel="noopener noreferrer">
                          Terms of Service
                        </a>{' '}
                        and order details.
                      </Checkbox>
                    </Form.Field>
                    <ButtonWrapper>
                      <Button
                        variant="primary"
                        fill
                        type="submit"
                        loading={isSubmitting}>
                        Confirm Payment Details
                      </Button>
                    </ButtonWrapper>
                  </Section>
                </Fieldset>
              </FormWrapper>
              <SummaryWrapper>{summaryUi}</SummaryWrapper>
            </Layout>
          </Form>
        )}
      </Formik>
    </Wrapper>
  )
}
